import React, { useState, useEffect, useContext } from "react";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import userContext from "../../context/UserContext";
import { useDarkMode } from "../../context/darkModeContext";

const Avatar1 = `${process.env.PUBLIC_URL}/assets/avatars/avatar1.webp`;
const Avatar2 = `${process.env.PUBLIC_URL}/assets/avatars/avatar2.webp`;
const Avatar3 = `${process.env.PUBLIC_URL}/assets/avatars/avatar3.webp`;

// Soft background colors for role cards
const ROLE_CARD_COLORS = [
  "#4f46e5",
  "#22c55e",
  "#ef4444",
  "#f59e0b",
  "#ec4899",
  "#06b6d4",
  "#8b5cf6",
  "#f97316",
];

const RoleCount = () => {
  const [roleCount, setRoleCount] = useState({});
  const context = useContext(userContext);
  const { darkMode } = useDarkMode();
  const { activeType, applyFilter, users } = context;

  useEffect(() => {
    const countedRoles = (users || []).reduce((acc, user) => {
      acc[user.role_priv] = (acc[user.role_priv] || 0) + 1;
      return acc;
    }, {});
    setRoleCount(countedRoles);
  }, [users]);

  const roleEntries = Object.entries(roleCount);

  return (
    <div
      style={{
        background: darkMode ? "#111827" : "#fff",
        borderRadius: 12,
        padding: "22px 24px",
        marginBottom: 18,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: darkMode ? "#f9fafb" : "#1a1a2e",
              margin: 0,
              letterSpacing: "-0.2px",
            }}
          >
            Administrative roles
          </h2>
          <p
            style={{
              fontSize: 12.5,
              color: darkMode ? "#9ca3af" : "#6b7280",
              lineHeight: 1.55,
              margin: "6px 0 0",
            }}
          >
            A role provides access to predefined menus and features so that
            depending on the assigned roles an administrator can have access to
            what he/she needs.
          </p>
        </div>

        <button
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            border: darkMode ? "1px solid #374151" : "1.5px solid #e5e7eb",
            background: darkMode ? "#1f2937" : "#fff",
            fontSize: 18,
            cursor: "pointer",
            color: "#6b7280",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "border-color 0.12s, color 0.12s",
          }}
        >
          +
        </button>
      </div>

      {activeType !== "all" && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 14,
          }}
        >
          <button
            onClick={() => applyFilter("all")}
            style={{
              height: 32,
              padding: "0 14px",
              fontSize: "12.5px",
              fontWeight: 500,
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              background: darkMode ? "#1f2937" : "#fff",
              color: darkMode ? "#f3f4f6" : "#374151",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Clear role filter ✕
          </button>
        </div>
      )}

      {/* Role cards grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 16,
        }}
      >
        {roleEntries.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: 13 }}>No roles found.</div>
        ) : (
          roleEntries.map(([role, count], index) => {
            const color = ROLE_CARD_COLORS[index % ROLE_CARD_COLORS.length];
            const isActive = activeType === role;
            return (
              <div
                key={role}
                onClick={() => applyFilter(role)}
                style={{
                  border: `1px solid ${
                    darkMode
                      ? isActive
                        ? color
                        : "#374151"
                      : isActive
                        ? color
                        : "#e5e7eb"
                  }`,
                  borderRadius: 12,
                  padding: "18px 20px",
                  cursor: "pointer",
                  transition: "border-color 0.15s, box-shadow 0.15s",
                  background: darkMode
                    ? isActive
                      ? color + "15"
                      : "#1f2937"
                    : isActive
                      ? color + "08"
                      : "#fff",
                  boxShadow: isActive ? `0 4px 12px ${color}20` : "none",
                  position: "relative",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.borderColor = "#c7d2fe";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                {/* Top row: count + avatar stack */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 14,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                    }}
                  >
                    {count} ACCOUNTS
                  </span>
                  <AvatarGroup max={3}>
                    <Avatar
                      alt="u1"
                      src={Avatar2}
                      sx={{ width: 20, height: 20, fontSize: 9 }}
                    />
                    <Avatar
                      alt="u2"
                      src={Avatar3}
                      sx={{ width: 20, height: 20, fontSize: 9 }}
                    />
                    <Avatar
                      alt="u3"
                      src={Avatar1}
                      sx={{ width: 20, height: 20, fontSize: 9 }}
                    />
                  </AvatarGroup>
                </div>

                {/* Role name */}
                <h3
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: darkMode ? "#f9fafb" : "#1a1a2e",
                    margin: "0 0 10px",
                  }}
                >
                  {role === "org_admin"
                    ? "Organization Admin"
                    : role
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (c) => c.toUpperCase())}
                </h3>

                {/* Privileges + actions row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 12.5, fontWeight: 500, color }}>
                    {role === "org_admin" ? "8" : "2"} privileges
                  </span>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        fontSize: 12,
                        color: darkMode ? "#9ca3af" : "#6b7280",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "color 0.12s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = darkMode
                          ? "#f9fafb"
                          : "#111827")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = darkMode
                          ? "#9ca3af"
                          : "#6b7280")
                      }
                    >
                      Manage members
                    </button>
                    <button
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        fontSize: 12,
                        color: darkMode ? "#9ca3af" : "#6b7280",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        transition: "color 0.12s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = darkMode
                          ? "#f9fafb"
                          : "#111827")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = darkMode
                          ? "#9ca3af"
                          : "#6b7280")
                      }
                    >
                      Edit privileges
                    </button>
                  </div>
                </div>

                {/* Active underline */}
                {isActive && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: color,
                      borderRadius: "0 0 12px 12px",
                    }}
                  />
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RoleCount;
